import { useState } from 'react'

import { Alert, Collapse, Divider, Text } from '@mantine/core'
import dayjs from 'dayjs'
import {
  Check,
  ChevronDown,
  ChevronRight,
  Loader,
  Pause,
  X,
} from 'lucide-react'

import DnDrawer from 'src/components/Core/DnDrawer'
import { DataMigrationPhase } from 'src/graphql/types/dataMigrations'
import {
  buildFullName,
  formatBytes,
  formatDateCustom,
  formatSnakeValue,
} from 'src/lib/formatters'
import {
  formatDuration,
  formatProviderLabel,
  formatSourceHost,
  isActivePhase,
  isStalled,
} from 'src/pages/SettingsPage/tabs/dataMigrationTab/lib/migrationDisplay'
import { DataMigrationRow } from 'src/pages/SettingsPage/tabs/dataMigrationTab/types'

import MigrationPhaseBadge from './MigrationPhaseBadge'

/**
 * Icon treatment per phase, following the VoE audit trail's config shape.
 * A green check on every row would claim a failed or cancelled run succeeded.
 *
 * `isCurrent` matters: every row is a transition the run has ALREADY made, so
 * a -ING phase is only still running if it is the newest entry and the row
 * still sits in it. Keying off the phase name alone left a spinner on every
 * past Downloading/Transforming step.
 */
const getPhaseIconConfig = (phase: string, isCurrent: boolean) => {
  if (phase === DataMigrationPhase.FAILED) {
    return {
      icon: <X size={12} />,
      iconClass: 'text-doubleNickel-error-500',
      bgClass: 'bg-doubleNickel-error-50',
    }
  }
  if (phase === DataMigrationPhase.CANCELLED) {
    return {
      icon: <Pause size={12} />,
      iconClass: 'text-doubleNickel-gray-500',
      bgClass: 'bg-doubleNickel-gray-100',
    }
  }
  if (isCurrent && isActivePhase(phase)) {
    return {
      icon: <Loader size={12} />,
      iconClass: 'text-doubleNickel-brand-500',
      bgClass: 'bg-doubleNickel-brand-25',
    }
  }
  return {
    icon: <Check size={12} />,
    iconClass: 'text-doubleNickel-success-600',
    bgClass: 'bg-doubleNickel-success-50',
  }
}

const Fact = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex flex-col gap-0.5">
    <div className="text-xs font-medium text-doubleNickel-gray-500">
      {label}
    </div>
    <div className="text-sm text-doubleNickel-gray-900">{value ?? '—'}</div>
  </div>
)

interface MigrationDetailDrawerProps {
  opened: boolean
  onClose: () => void
  migration: DataMigrationRow | null
}

const MigrationDetailDrawer = ({
  opened,
  onClose,
  migration,
}: MigrationDetailDrawerProps) => {
  const [historyExpanded, setHistoryExpanded] = useState(true)

  if (!migration) {
    return (
      <DnDrawer opened={opened} onClose={onClose} stackId={undefined}>
        {null}
      </DnDrawer>
    )
  }

  const sourceHost = formatSourceHost(migration.sourceUrl)
  const initiatedByName = buildFullName(migration.initiatedBy)

  // Chronological, so each entry can report how long the previous phase took.
  const phaseLogs = [...(migration.phaseLogs ?? [])].sort((a, b) =>
    dayjs(a.createdAt).diff(dayjs(b.createdAt))
  )

  return (
    <DnDrawer opened={opened} onClose={onClose} stackId={undefined}>
      {/* Layout mirrors EmploymentDetailsDrawer so drawers read the same */}
      <div className="flex h-screen flex-col space-y-4 overflow-y-auto pb-14">
        {/* HEADER */}
        <div className="flex flex-row items-center justify-between gap-3">
          <Text size="lg" fw={500} className="truncate">
            {migration.migrationId}
          </Text>
          <MigrationPhaseBadge
            phase={migration.phase}
            errorMessage={migration.errorMessage}
            stalled={isStalled(migration)}
          />
        </div>
        <Text size="xs" c="dimmed">
          Started {dayjs(migration.createdAt).format('MMM D, YYYY h:mm A')}
          {initiatedByName ? ` by ${initiatedByName}` : ''}
        </Text>

        <Divider />

        {migration.phase === DataMigrationPhase.FAILED &&
          migration.errorMessage && (
            <Alert color="red" variant="light" title="Migration failed">
              <span className="break-words text-sm">
                {migration.errorMessage}
              </span>
            </Alert>
          )}

        {/* Key facts */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-lg border border-doubleNickel-gray-200 p-4">
          <Fact
            label="Provider"
            value={formatProviderLabel(migration.provider)}
          />
          {/* Host only, never the full URL: vendor export links are commonly
              signed/tokenized, so the path is treated as a credential. */}
          <Fact label="Download host" value={sourceHost} />
          <Fact label="Source file" value={migration.sourceFilename} />
          <Fact
            label="Raw files"
            value={
              migration.rawFileCount != null
                ? `${migration.rawFileCount}${
                    migration.rawTotalBytes != null
                      ? ` (${formatBytes(migration.rawTotalBytes)})`
                      : ''
                  }`
                : null
            }
          />
          <Fact
            label="Transformed files"
            value={migration.transformedFileCount}
          />
          <Fact label="Total applicants" value={migration.totalApplicants} />
          <Fact label="Entries created" value={migration.entriesCreatedCount} />
          <Fact
            label="Batch size / parallel"
            value={`${migration.batchSize} / ${migration.parallel}`}
          />
        </div>

        {/* Mirrors the VoE AUDIT TRAIL section: collapsible xs-caps header with
            a count, hairline rule, and rows of icon | title + right-aligned
            date | description. */}
        <div className="flex items-center justify-between">
          <button
            className="flex items-center gap-1 text-doubleNickel-gray-500 hover:text-doubleNickel-gray-700"
            onClick={() => setHistoryExpanded((v) => !v)}
          >
            {historyExpanded ? (
              <ChevronDown size={12} />
            ) : (
              <ChevronRight size={12} />
            )}
            <Text size="xs" fw={400} className="text-inherit">
              PHASE HISTORY
              <span className="ml-1 text-doubleNickel-gray-400">
                ({phaseLogs.length})
              </span>
            </Text>
          </button>
        </div>

        <Collapse in={historyExpanded}>
          {phaseLogs.length > 0 ? (
            <div className="relative pb-4 pl-4" data-testid="phase-history">
              {/* Vertical line */}
              <div className="absolute bottom-4 left-[11px] top-0 w-px bg-doubleNickel-gray-200" />

              <div className="flex flex-col gap-3">
                {phaseLogs.map((log, index) => {
                  // Newest entry, and the row hasn't moved past it since.
                  const isCurrent =
                    index === phaseLogs.length - 1 &&
                    log.toPhase === migration.phase
                  const config = getPhaseIconConfig(log.toPhase, isCurrent)
                  const previousLog = index > 0 ? phaseLogs[index - 1] : null
                  return (
                    <div
                      key={log.dataMigrationPhaseLogId}
                      className="relative flex gap-2.5"
                    >
                      {/* Icon bullet */}
                      <div
                        className={`relative z-10 -ml-4 flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full ${config.bgClass}`}
                      >
                        <span className={config.iconClass}>{config.icon}</span>
                      </div>

                      {/* Content */}
                      <div className="flex min-w-0 flex-1 flex-col gap-0.5 pt-0.5">
                        <div className="flex items-center justify-between">
                          <Text
                            size="xs"
                            fw={500}
                            className="text-doubleNickel-gray-900"
                          >
                            {formatSnakeValue(log.toPhase)}
                          </Text>
                          <Text
                            size="xs"
                            className="text-doubleNickel-gray-400"
                          >
                            {formatDateCustom(
                              log.createdAt,
                              'MM/DD/YYYY h:mm A'
                            )}
                          </Text>
                        </div>
                        {previousLog && (
                          <Text
                            size="xs"
                            className="text-doubleNickel-gray-600"
                          >
                            {formatDuration(
                              previousLog.createdAt,
                              log.createdAt
                            )}{' '}
                            in {formatSnakeValue(previousLog.toPhase)}
                          </Text>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ) : (
            <Text
              size="xs"
              className="py-1 text-center text-doubleNickel-gray-400"
            >
              No phase changes recorded yet
            </Text>
          )}
        </Collapse>
      </div>
    </DnDrawer>
  )
}

export default MigrationDetailDrawer
